import './style.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';

import App from './App.vue';
import router from './router';
import i18n from './plugins/i18n';
import { preloadTinyMceOnIdle } from './tinymce/loadScript.js';

// ---------------------------------------------------------------------------
// Stale-chunk auto-recovery (copied verbatim from booking-engine pattern).
// After a deploy, old JS chunk hashes vanish; we catch preload + lazy-import
// failures in three places and refresh once (rate-limited via sessionStorage).
// ---------------------------------------------------------------------------

const CHUNK_RELOAD_KEY = '__chunk_reload';
const CHUNK_RELOAD_COOLDOWN = 10_000;

function shouldAutoReload() {
  const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  return !lastReload || Date.now() - Number(lastReload) >= CHUNK_RELOAD_COOLDOWN;
}

function doChunkReload() {
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
}

function looksLikeChunkError(reason) {
  if (!reason) return false;
  const msg = typeof reason === 'string' ? reason : reason.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes("'text/html' is not a valid JavaScript MIME type") ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk')
  );
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (shouldAutoReload()) doChunkReload();
});

window.addEventListener('unhandledrejection', (event) => {
  if (looksLikeChunkError(event.reason) && shouldAutoReload()) {
    event.preventDefault();
    doChunkReload();
  }
});

// ---------------------------------------------------------------------------
// App init
// ---------------------------------------------------------------------------

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);
app.use(i18n);
app.use(Toast, {
  position: 'bottom-right',
  timeout: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  maxToasts: 2,
  newestOnTop: true,
});

app.mount('#app');

// Best-effort idle preload for TinyMCE so the first blog/promotion edit feels
// instant.
preloadTinyMceOnIdle();
